import {
  CREATE_COMMENT_REACTION,
  CommentReactionId,
  DELETE_COMMENT_REACTION,
  groupBy,
  nullthrows,
} from "@hex/common";
import React, { useCallback, useMemo } from "react";
import styled from "styled-components";
import { v4 as uuid } from "uuid";

import { CommentModelFragment } from "../../hex-version-multiplayer/handlers/commentClientHandlers.generated";
import { useCurrentUser } from "../../hooks/me/useCurrentUser";
import { useHexVersionAOContext } from "../../util/hexVersionAOContext";

import {
  CommentReaction,
  CommentReactionClickHandlerArgs,
} from "./CommentReaction";
import { CommentReactionButton } from "./CommentReactionButton";

export interface CommentReactionListProps {
  readonly commentId: string;
  readonly reactions: CommentModelFragment["reactions"];
}

const ContainerDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  align-items: center;
  margin-top: 2px;
`;

export const CommentReactionList: React.ComponentType<CommentReactionListProps> =
  React.memo(function CommentReactionList({
    commentId,
    reactions,
  }: CommentReactionListProps) {
    const { dispatchAO } = useHexVersionAOContext();
    const currentUser = useCurrentUser();
    const [reactionsByContent, sortedReactionContent] = useMemo(() => {
      const groupedReactions = groupBy(reactions, "content");
      const sortedKeys = Object.keys(groupedReactions).sort((c1, c2) => {
        return groupedReactions[c2].length - groupedReactions[c1].length;
      });
      return [groupedReactions, sortedKeys];
    }, [reactions]);

    const onReactionClick = useCallback(
      (args: CommentReactionClickHandlerArgs) => {
        if (currentUser == null) {
          return;
        } else if (args.type === "create") {
          dispatchAO(
            CREATE_COMMENT_REACTION.create({
              commentId,
              creatorNameOrEmail: currentUser.name ?? currentUser.email,
              creatorId: currentUser.id,
              commentReactionId: uuid() as CommentReactionId,
              content: args.content,
            }),
          );
        } else if (args.type === "delete") {
          dispatchAO(
            DELETE_COMMENT_REACTION.create({
              commentId,
              creatorId: currentUser.id,
              commentReactionId: args.reactionId,
              content: args.content,
            }),
          );
        }
      },
      [commentId, currentUser, dispatchAO],
    );

    if (reactions.length === 0 && currentUser == null) {
      return null;
    }

    return (
      <ContainerDiv>
        {sortedReactionContent.map((content) => (
          <CommentReaction
            key={content}
            currentUserId={nullthrows(currentUser).id}
            reactions={reactionsByContent[content]}
            onClick={onReactionClick}
          />
        ))}
        <CommentReactionButton commentId={commentId} reactions={reactions} />
      </ContainerDiv>
    );
  });
